import type { NavigationGuardReturn, RouteLocationNormalized } from "vue-router"
import { ensureEmbedAuthenticated } from "@/router/guardians"
import { useCompanyStore } from "@/store/company-store"
import useAsclepius from "@common/composables/api/asclepius"
import { isEmbeddedContext, sendToParent } from "@common/services/embed-service"

/**
 * Shared embed bootstrap: validates embedded context, authenticates,
 * and loads employer company data into the company store.
 *
 * Individual embed route records should call this first, then load
 * any widget-specific store data they need.
 */
export async function bootstrapEmbedContext(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): Promise<NavigationGuardReturn> {
  if (!isEmbeddedContext()) return { name: "home" }

  const authResult = await ensureEmbedAuthenticated(to, from)
  if (authResult !== true) return authResult

  try {
    const { getAuthenticationApi } = useAsclepius()
    const { data } = await getAuthenticationApi().getEmployerContext()

    if (!data.company) {
      console.error("[embed] Employer context returned no company")
      sendToParent("AUTH_ERROR", {
        code: "EMBED_NO_COMPANY",
        message: "Employer context has no associated company"
      })
      return false
    }

    useCompanyStore().updateCompany(data.company)
  } catch (error) {
    console.error("[embed] Failed to bootstrap embed context:", error)
    sendToParent("AUTH_ERROR", {
      code: "EMBED_BOOTSTRAP_FAILED",
      message: "Failed to initialize embed state"
    })
    return false
  }

  return true
}
